import { useState, useEffect, useCallback, type ReactNode } from 'react';
import type { NavGroup, UserInfo, BreadcrumbItem, AppShellLabels } from './types';
import { HeaderBar } from './HeaderBar';
import { SideNav } from './SideNav';

const STORAGE_KEY = 'eraf.sidenav.collapsed';
const LANG_STORAGE_KEY = 'eraf.lang';
const BREAKPOINT_MD = 768;

interface AppShellProps {
  sideNavItems: NavGroup[];
  user: UserInfo;
  currentPath: string;
  breadcrumb: BreadcrumbItem[];
  onLogout: () => void;
  onProfileClick: () => void;
  onPasswordChange: () => void;
  onActivityHistory?: () => void;
  onNavigate?: (path: string) => void;
  version: string;
  buildHash?: string;
  children: ReactNode;
  /** R3-001: 다크모드 토글 슬롯 (apps/web 의 ThemeToggle 등 주입) */
  themeToggle?: ReactNode;
  /** #659: 헤더 우측 액션 슬롯(전역 검색 버튼 등) — apps/web 에서 주입 */
  headerActions?: ReactNode;
  /** R6-002: i18n 라벨 — ui-kit 은 framework-agnostic 이므로 apps/web 에서 주입 */
  labels?: AppShellLabels;
}

function getInitialCollapsed(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.innerWidth < BREAKPOINT_MD) return true;
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'true') return true;
  if (stored === 'false') return false;
  return false;
}

function getInitialLang(): 'ko' | 'en' {
  const stored = localStorage.getItem(LANG_STORAGE_KEY);
  if (stored === 'en') return 'en';
  return 'ko';
}

export function AppShell({
  sideNavItems,
  user,
  currentPath,
  breadcrumb,
  onLogout,
  onProfileClick,
  onPasswordChange,
  onActivityHistory,
  onNavigate,
  version,
  buildHash,
  children,
  themeToggle,
  headerActions,
  labels,
}: AppShellProps) {
  const [collapsed, setCollapsed] = useState<boolean>(getInitialCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < BREAKPOINT_MD : false
  );
  const [lang, setLang] = useState<'ko' | 'en'>(getInitialLang);

  // 반응형: 윈도우 크기 변경 감지
  useEffect(() => {
    const handler = () => {
      const mobile = window.innerWidth < BREAKPOINT_MD;
      setIsMobile(mobile);
      if (mobile) {
        setCollapsed(true);
        setMobileOpen(false);
      }
    };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // [ 키 토글 단축키
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.key === '[') {
        e.preventDefault();
        toggle();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  });

  const toggle = useCallback(() => {
    if (isMobile) {
      setMobileOpen((v) => !v);
    } else {
      setCollapsed((v) => {
        const next = !v;
        localStorage.setItem(STORAGE_KEY, String(next));
        return next;
      });
    }
  }, [isMobile]);

  const handleLangChange = useCallback((next: 'ko' | 'en') => {
    setLang(next);
    localStorage.setItem(LANG_STORAGE_KEY, next);
    document.documentElement.setAttribute('lang', next);
    // R1-002 fix: i18next 의 locale 변경 propagation
    // i18n module 이 apps/web 에서만 init 되므로 dynamic import (ui-kit 은 라이브러리 의존 X)
    if (typeof window !== 'undefined') {
      const w = window as Window & { __eraf_i18n?: { changeLanguage: (l: string) => void } };
      if (w.__eraf_i18n) {
        w.__eraf_i18n.changeLanguage(next);
      }
    }
  }, []);

  // 모바일 오버레이 클릭 시 닫기
  const handleOverlayClick = useCallback(() => {
    setMobileOpen(false);
  }, []);

  // 모바일에서 nav 키 Escape로 닫기
  useEffect(() => {
    if (!mobileOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [mobileOpen]);

  const sideNavWidth = collapsed
    ? 'var(--layout-side-nav-width-collapsed)'
    : 'var(--layout-side-nav-width-expanded)';

  const showSidePanel = !isMobile || mobileOpen;

  return (
    <div
      style={{
        display: 'flex',
        minHeight: '100vh',
        background: 'var(--color-surface-background)',
      }}
    >
      {/* 모바일 오버레이 */}
      {isMobile && mobileOpen && (
        <div
          aria-hidden="true"
          onClick={handleOverlayClick}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'var(--color-overlay-backdrop)',
            zIndex: 149,
          }}
        />
      )}

      {/* 사이드 패널 */}
      {showSidePanel && (
        <aside
          aria-label={labels?.sidenavAriaLabel ?? '사이드 내비게이션'}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            bottom: 0,
            width: isMobile
              ? 'var(--layout-side-nav-width-expanded)'
              : sideNavWidth,
            background: 'var(--color-sidebar-bg)',
            borderRight: '1px solid var(--color-sidebar-hover-bg)',
            zIndex: 150,
            display: 'flex',
            flexDirection: 'column',
            transition: 'width 0.2s ease',
            boxShadow: isMobile ? 'var(--shadow-lg)' : 'none',
          }}
        >
          <SideNav
            groups={sideNavItems}
            currentPath={currentPath}
            collapsed={!isMobile && collapsed}
            onNavigate={(path) => {
              if (isMobile) setMobileOpen(false);
              onNavigate?.(path);
            }}
            sidenavAriaLabel={labels?.sidenavAriaLabel}
            brandName={labels?.brandName}
            groupCollapseLabel={labels?.sidenavGroupCollapseLabel}
            groupExpandLabel={labels?.sidenavGroupExpandLabel}
          />
        </aside>
      )}

      {/* 메인 콘텐츠 영역 */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          marginLeft: isMobile ? 0 : sideNavWidth,
          transition: 'margin-left 0.2s ease',
        }}
      >
        {/* 상단 헤더 */}
        <HeaderBar
          onToggle={toggle}
          breadcrumb={breadcrumb}
          onBreadcrumbNavigate={onNavigate}
          user={user}
          onLogout={onLogout}
          onProfileClick={onProfileClick}
          onPasswordChange={onPasswordChange}
          onActivityHistory={onActivityHistory}
          lang={lang}
          onLangChange={handleLangChange}
          themeToggle={themeToggle}
          headerActions={headerActions}
          labels={labels}
        />

        {/* 콘텐츠 */}
        <main
          id="main-content"
          tabIndex={-1}
          aria-label={labels?.mainAriaLabel ?? '주 콘텐츠 영역'}
          style={{
            flex: 1,
            marginTop: 'var(--layout-header-height)',
            padding: 'var(--spacing-6)',
            maxWidth: '1440px',
            width: '100%',
            alignSelf: 'center',
            boxSizing: 'border-box',
          }}
        >
          {children}
        </main>

        {/* 푸터 */}
        <footer
          style={{
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 'var(--spacing-4)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-muted)',
            borderTop: '1px solid var(--color-surface-border)',
            padding: '0 var(--spacing-6)',
            flexShrink: 0,
          }}
        >
          <span>{labels?.footerCopyright ?? '© 2025 SK AX — ERAF'}</span>
          <span aria-hidden="true">·</span>
          <span>{labels?.footerVersionLabel ? labels.footerVersionLabel(version) : `v${version}`}</span>
          {buildHash && (
            <>
              <span aria-hidden="true">·</span>
              <span
                title={labels?.footerBuildLabel ?? '빌드 해시'}
                style={{ fontFamily: 'var(--font-family-mono)' }}
              >
                {buildHash.slice(0, 8)}
              </span>
            </>
          )}
        </footer>
      </div>

      {/* 반응형 미디어 스타일 */}
      <style>{`
        @media (max-width: 768px) {
          #main-content {
            padding: var(--spacing-4) !important;
          }
        }
      `}</style>
    </div>
  );
}
